/**
 * Firebase App Initialization Module
 * 
 * Simplified Firebase initialization with modern SDK practices
 */

import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { 
  getFirestore, 
  connectFirestoreEmulator
} from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getFirebaseConfig, shouldUseEmulators, getEmulatorConfig } from './FirebaseConfig';

const isClient = typeof window !== 'undefined';

// Firebase instance cache
let firebaseApp = null;
let firebaseAuth = null;
let firebaseFirestore = null;
let firebaseFunctions = null;

// Initialization states
let appInitializationPromise = null;

// Get the Firebase configuration at module load
const firebaseConfig = getFirebaseConfig();

/**
 * Initialize Firebase App
 * @returns {Promise<Object>} Promise that resolves to Firebase app instance
 */
export function initializeFirebaseApp() {
  if (!isClient) {
    return Promise.resolve(null);
  }
  // If we already have an initialization in progress, return that promise
  if (appInitializationPromise) {
    return appInitializationPromise;
  }
  
  // If app is already fully initialized, return a resolved promise
  if (firebaseApp && firebaseAuth && firebaseFirestore && firebaseFunctions) {
    return Promise.resolve(firebaseApp);
  }
  
  // Start initialization process
  appInitializationPromise = new Promise(async (resolve, reject) => {
    try {
      console.debug('Initializing Firebase App...');
      
      // Step 1: Initialize the Firebase app if not already done
      if (!firebaseApp) {
        firebaseApp = initializeApp(firebaseConfig);
        console.debug('Firebase App core initialized');
      }
      
      // Step 2: Initialize authentication
      if (!firebaseAuth) {
        firebaseAuth = getAuth(firebaseApp);
        console.debug('Firebase Auth initialized');
      }
      
      // Step 3: Initialize Firestore (simplified)
      if (!firebaseFirestore) {
        firebaseFirestore = getFirestore(firebaseApp);
        console.debug('Firebase Firestore initialized');
      }
      
      // Step 4: Initialize Cloud Functions
      if (!firebaseFunctions) {
        firebaseFunctions = getFunctions(firebaseApp);
        console.debug('Firebase Functions initialized');
      }
      
      // Step 5: Connect to emulators in development mode
      if (shouldUseEmulators()) {
        await connectToEmulators();
      }
      
      console.debug('Firebase App initialized successfully');
      resolve(firebaseApp);
    } catch (error) {
      console.error('Error initializing Firebase App:', error);
      // Reset the initialization promise so it can be tried again
      appInitializationPromise = null;
      reject(error);
    }
  });
  
  // Once initialization is complete (success or failure), clear the promise
  appInitializationPromise.finally(() => {
    appInitializationPromise = null;
  });
  
  return appInitializationPromise;
}

/**
 * Connect to Firebase emulators in development mode
 * @returns {Promise<void>} Promise that resolves when emulators are connected
 */
async function connectToEmulators() {
  if (!isClient) {
    return Promise.resolve();
  }
  console.debug('Connecting to Firebase emulators...');
  
  const emulatorConfig = getEmulatorConfig();
  
  try {
    // Connect to Auth emulator
    connectAuthEmulator(
      firebaseAuth, 
      `http://${emulatorConfig.auth.host}:${emulatorConfig.auth.port}`,
      { disableWarnings: true }
    );
    console.debug(`Connected to Auth emulator on ${emulatorConfig.auth.host}:${emulatorConfig.auth.port}`);
  } catch (error) {
    if (!error.message.includes('already been called')) {
      console.warn('Error connecting to Auth emulator:', error);
    }
  }
  
  try {
    // Connect to Functions emulator
    connectFunctionsEmulator(
      firebaseFunctions, 
      emulatorConfig.functions.host,
      emulatorConfig.functions.port
    );
    console.debug(`Connected to Functions emulator on ${emulatorConfig.functions.host}:${emulatorConfig.functions.port}`);
  } catch (error) {
    if (!error.message.includes('already been called')) {
      console.warn('Error connecting to Functions emulator:', error);
    }
  }
  
  try {
    // Connect to Firestore emulator
    connectFirestoreEmulator(
      firebaseFirestore,
      emulatorConfig.firestore.host,
      emulatorConfig.firestore.port
    );
    console.debug(`Connected to Firestore emulator on ${emulatorConfig.firestore.host}:${emulatorConfig.firestore.port}`);
  } catch (error) {
    if (!error.message.includes('already been called')) {
      console.warn('Error connecting to Firestore emulator:', error);
    }
  }
  
  return Promise.resolve();
}

/**
 * Get Firebase App instance
 * @returns {Promise<Object>} Promise that resolves to Firebase app instance
 */
export async function getFirebaseApp() {
  if (!isClient) {
    return null;
  }
  if (!firebaseApp) {
    return await initializeFirebaseApp();
  }
  return Promise.resolve(firebaseApp);
}

/**
 * Get Firebase Auth instance
 * @returns {Object} Firebase auth instance
 */
export function getFirebaseAuth() {
  if (!isClient) {
    throw new Error("Firebase Auth is not available in this environment");
  }
  if (!firebaseAuth) {
    // Start initialization but don't wait for it to complete
    initializeFirebaseApp().catch(error => {
      console.error("Error during async Firebase initialization:", error);
    });
    
    // If we still don't have auth, throw error
    if (!firebaseAuth) {
      throw new Error("Firebase Auth not initialized yet");
    }
  }
  return firebaseAuth;
}

/**
 * Get Firebase Firestore instance
 * @returns {Object} Firebase firestore instance
 */
export function getFirebaseFirestore() {
  if (!isClient) {
    throw new Error("Firebase Firestore is not available in this environment");
  }
  if (!firebaseFirestore) {
    // Start initialization but don't wait for it to complete
    initializeFirebaseApp().catch(error => {
      console.error("Error during async Firebase initialization:", error);
    });
    
    // If we still don't have firestore, throw error
    if (!firebaseFirestore) {
      throw new Error("Firebase Firestore not initialized yet");
    }
  }
  return firebaseFirestore;
}

/**
 * Get Firebase Firestore instance with guaranteed initialization
 * @returns {Promise<Object>} Promise that resolves to Firestore instance
 */
export async function getInitializedFirestore() {
  if (!isClient) {
    return null;
  }
  await initializeFirebaseApp();
  return firebaseFirestore;
}

/**
 * Get Firebase Functions instance
 * @returns {Object} Firebase functions instance
 */
export function getFirebaseFunctions() {
  if (!isClient) {
    throw new Error("Firebase Functions is not available in this environment");
  }
  if (!firebaseFunctions) {
    // Start initialization but don't wait for it to complete
    initializeFirebaseApp().catch(error => {
      console.error("Error during async Firebase initialization:", error);
    });
    
    // If we still don't have functions, throw error
    if (!firebaseFunctions) {
      throw new Error("Firebase Functions not initialized yet");
    }
  }
  return firebaseFunctions;
}

/**
 * Check if Firebase is fully initialized
 * @returns {boolean} true if Firebase is fully initialized
 */
export function isFirebaseInitialized() {
  return !!firebaseApp && !!firebaseAuth && !!firebaseFirestore && !!firebaseFunctions;
}

/**
 * Wait for Firebase to be fully initialized
 * @returns {Promise<boolean>} Promise that resolves when Firebase is fully initialized
 */
export async function waitForFirebaseInitialization() {
  if (!isClient) {
    return false;
  }
  await initializeFirebaseApp();
  return true;
}
